import React, { useEffect, useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowIcon } from "../../../assets/icons/Arrow-down.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/Fill 1.svg";
import { CircularProgress } from "@mui/material";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Table,
  DialogTitle,
  Menu,
  DialogContent,
  IconButton,
  TableBody,
  Grid,
  styled,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "../PaginationComp/Paginations";
import DatabaseApi from "../../../services/database_api";
import axios from "axios";

interface Ticket {
  id: number;
  user_id: number | null;
  driver_id: string | null;
  ticket_no: string | null;
  subject: string;
  message: string;

  screenshot: string | null;
  other_attachments: string | null;

  status: string;
  department: string;
  created_at: string;
  updated_at: string;
  driver_name: string | null;
  user_name: string | null;
  user_email: string | null;
  driver_email: string | null;
}

// styled //
const Label = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const Value = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
}));

const AttachmentBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  height: 200,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
}));

const SupportTicket: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<Ticket | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOrder, setSortOrder] = useState("");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketType, setTicketType] = useState<"user" | "driver">("user");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [ticketId, setTicketId] = useState<string>("");
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchTickets = async (
    pageNumber: number,
    type: "user" | "driver",
    searchQuery: string,
    ticketId: string,
    sortType?: "old" | "new"
  ) => {
    setLoading(true);
    setError(null);

    const apiUrl = DatabaseApi.getTicketsByAdminUrl(pageNumber, type);
    const url = DatabaseApi.searchTickets(pageNumber, searchQuery, type);
    const token = localStorage.getItem("authToken");

    console.log("Fetch Tickets Params:", {
      pageNumber,
      type,
      searchQuery,
      ticketId,
      apiUrl,
      url,
      token: token ? "Token present" : "No token",
    });
    try {
      const response = await axios.get(`${apiUrl}$search=${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          sort: sortType, // Add sort parameter to the request
        },
      });

      const ticketData = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      setTickets(ticketData);
      setTotalPages(response.data.last_page || 1);
    } catch (error) {
      console.error("Error fetching tickets data:", error);
      setError("Failed to fetch tickets. Please try again.");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (ticketId: number) => {
    setUpdatingStatus(true);
    const token = localStorage.getItem("authToken");

    try {
      const ResolveStatus = DatabaseApi.updateTicketStatus(
        ticketId.toString(),
        "Resolved"
      );

      console.log("API URL:", ResolveStatus);

      const response = await axios.put(
        ResolveStatus,
        {
          id: ticketId.toString(),
          status: "Resolved",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Response:", response.data);

      // Modify the success check to be more flexible
      if (response.data && (response.data.success || response.status === 200)) {
        // Update the local state to reflect the change
        setTickets((prevTickets) =>
          prevTickets.map((ticket) =>
            ticket.id === ticketId ? { ...ticket, status: "Resolved" } : ticket
          )
        );

        // If the current selected ticket is the one being updated, update it too
        setSelectedUser((prev) =>
          prev && prev.id === ticketId ? { ...prev, status: "Resolved" } : prev
        );

        // Refresh the tickets list to get updated data
        await fetchTickets(
          currentPage,
          ticketType,
          searchQuery,
          ticketId.toString()
        );
        alert("Ticket status updated successfully!");

        setTimeout(() => {
          setShowModal(false);
        }, 1000);
      } else {
        console.error("Update failed:", response);
        throw new Error(
          response.data.message || "Failed to update ticket status"
        );
      }
    } catch (error: any) {
      if (error.response) {
        alert(
          `Failed to update ticket status: ${
            error.response.data.message || "Unknown error"
          }`
        );
      } else if (error.request) {
        alert("No response from server. Please check your network connection.");
      } else {
        alert(`Error: ${error.message}`);
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Debounce search to prevent too many API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Wait 500ms after last keystroke before searching
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Convert sortOrder to sort type
    const sortType =
      sortOrder === "asc" ? "old" : sortOrder === "desc" ? "new" : "";

    fetchTickets(currentPage, ticketType, debouncedSearchQuery, sortType);
  }, [currentPage, ticketType, debouncedSearchQuery, sortOrder]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const handleSortSelection = (order: "asc" | "desc") => {
    setSortOrder(order);
    handleCloseMenu();

    // Convert asc/desc to old/new
    const sortType = order === "asc" ? "old" : "new";
    fetchTickets(currentPage, ticketType, searchQuery, sortType);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Update the placeholder text based on ticket type
  const getSearchPlaceholder = () => {
    return ticketType === "driver"
      ? "Search Drivers by Name"
      : "Search Users by Name";
  };

  const renderAttachment = (attachment: string | null) => {
    if (!attachment) return "No attachment";

    const fullPath = DatabaseApi.getTicketDoc(attachment);

    if (fullPath.endsWith(".txt")) {
      return (
        <a href={fullPath} target="_blank" rel="noopener noreferrer">
          View Text File
        </a>
      );
    } else {
      return (
        <img
          src={fullPath}
          alt="Attachment"
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onClick={() => handleImageClick(fullPath)}
        />
      );
    }
  };

  const handleImageClick = (src: string) => {
    setModalImage(src);
  };

  const closeModal = () => {
    setModalImage(null);
  };

  const handleIconClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation(); // Prevent event from propagating to the Select component
    setIsOpen(!isOpen);
  };

  //Pagination
  const [itemsPerPage] = useState(10); // Or any other number you prefer
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tickets.slice(indexOfFirstItem, indexOfLastItem);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <div
      className="p-6 bg-white min-h-screen"
      style={{
        maxWidth: "90%",
        marginLeft: "237px",
        marginTop: "39px",
        fontFamily: "Montserrat",
      }}
    >
      <Box
        sx={{ p: 3.5, bgcolor: "background.paper", fontFamily: "Montserrat" }}
        color="#2C3E50"
      >
        <div className="flex justify-between items-center mb-4">
          <Typography
            variant="h5"
            gutterBottom
            color="#2C3E50"
            style={{ fontFamily: "Montserrat", fontWeight: "600" }}
          >
            Support Tickets
          </Typography>
          <div className="flex items-center gap-4">
            <Select
              open={isOpen}
              onClose={() => setIsOpen(false)}
              onOpen={() => setIsOpen(true)}
              style={{ fontFamily: "Montserrat" }}
              value={ticketType}
              onChange={(e) =>
                setTicketType(e.target.value as "user" | "driver")
              }
              IconComponent={() => (
                <div
                  onClick={handleIconClick}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginRight: "8px",
                  }}
                >
                  <ArrowIcon
                    style={{
                      fontFamily: "Montserrat",
                      width: "2rem",
                      height: "2rem",
                      marginRight: "8px",
                      color: "#FDA214",
                      transition: "transform 0.3s ease",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </div>
              )}
              sx={{
                width: 200,
                height: "3rem",
                fontFamily: "Montserrat",
                borderRadius: "10px",
                "& fieldset": {
                  borderRadius: "10px",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FDA214", // Custom border color on focus
                  boxShadow: "0 0 0 2px #FDA214", // Custom box shadow on focus
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FDA214", // Optional: set border color on hover
                },
              }}
            >
              <MenuItem value="user" style={{ fontFamily: "Montserrat" }}>
                User Tickets
              </MenuItem>
              <MenuItem value="driver" style={{ fontFamily: "Montserrat" }}>
                Driver Tickets
              </MenuItem>
            </Select>

            <div className="relative ">
              <SearchIcon
                className="absolute left-4 top-1/2 transform -translate-y-1/2"
                style={{
                  width: "20px",
                  height: "20px",
                  color: "#FDA214",
                  fontFamily: "Montserrat",
                }} // Adjust size and color if necessary
              />
              <Form.Control
                type="text"
                placeholder={getSearchPlaceholder()}
                className="pl-10 pr-6 py-2 w-112 rounded-full border border-gray-300"
                value={searchQuery}
                onChange={handleSearch}
                style={{
                  width: "30rem",
                  fontFamily: "Montserrat",
                  height: "3.5rem",
                  lineHeight: "3rem",
                  borderRadius: "20px",
                  // textAlign: "center",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                }} // Custom width and height
              />
              <style>
                {`
                  .relative input::placeholder {
                  color: #B4BDC4; // Placeholder color
                  }
                 `}
              </style>
            </div>
          </div>
        </div>

        {loading && (
          <Typography style={{ fontFamily: "Montserrat" }}>
            Loading...
          </Typography>
        )}
        {error && (
          <Typography color="error" style={{ fontFamily: "Montserrat" }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "120px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      bgcolor: "#F0F3FA",
                    }}
                  >
                    Ticket No.
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "150px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      bgcolor: "#F0F3FA",
                    }}
                  >
                    First Name
                  </TableCell>
                  <TableCell
                    sx={{
                      width: "150px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      bgcolor: "#F0F3FA",
                    }}
                  >
                    Department
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: "200px",
                      fontWeight: "bold",
                      bgcolor: "#F0F3FA",
                    }}
                  >
                    Subject
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: "180px",
                      whiteSpace: "nowrap",
                      fontWeight: "bold",
                      bgcolor: "#F0F3FA",
                    }}
                  >
                    Date Created
                    <IconButton onClick={handleOpenMenu} size="small">
                      <KeyboardArrowDown />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                    >
                      <MenuItem onClick={() => handleSortSelection("asc")}>
                        Earliest
                      </MenuItem>
                      <MenuItem onClick={() => handleSortSelection("desc")}>
                        Latest
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems
                  .filter(
                    (ticket) =>
                      (ticketType === "driver" && ticket.driver_id) ||
                      (ticketType === "user" && ticket.user_id)
                  )
                  .map((ticket) => (
                    <TableRow
                      key={ticket.id}
                      onClick={() => {
                        setSelectedUser(ticket);
                        setShowModal(true);
                      }}
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          hoveredRow === ticket.id ? "#f5f5f5" : "transparent",
                        "&:hover": {
                          bgcolor: "#f5f5f5",
                        },
                      }}
                      onMouseEnter={() => setHoveredRow(ticket.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {ticket.ticket_no || "N/A"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {ticketType === "driver" && ticket.driver_name
                          ? ticket.driver_name
                          : ticketType === "user" && ticket.user_name
                          ? ticket.user_name
                          : ""}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {ticket.department}
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {ticket.subject}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {new Date(ticket.created_at)
                          .toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                          .replace(/\//g, "-")
                          .toUpperCase()}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <div
          className="d-flex justify-content-center  align-items-center mt-4 flex-wrap p-3 rounded-lg"
          style={{ height: "100px", fontFamily: "Montserrat" }}
        >
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
        {/* Modal for image enlargement */}
        {modalImage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1500,
            }}
            onClick={closeModal}
          >
            <img
              src={modalImage}
              alt="Enlarged Attachment"
              style={{ maxWidth: "90%", maxHeight: "90%" }}
            />
          </div>
        )}
        {selectedUser && (
          <Modal show={showModal} onHide={handleCloseMenu} size="lg">
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="h6"
                  color="#2C3E50"
                  style={{ fontFamily: "Montserrat" }}
                >
                  Ticket Details
                </Typography>
                <IconButton onClick={handleCloseModal} size="small">
                  <CloseIcon sx={{ color: "orange" }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Label style={{ color: "#4D667C", fontFamily: "Montserrat" }}>
                    Full Name :{" "}
                    {ticketType === "user"
                      ? selectedUser.user_name
                      : selectedUser.driver_name}
                  </Label>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Label
                    style={{
                      color: "#212830",
                      fontFamily: "Montserrat",
                    }}
                  >
                    email:{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                      }}
                    >
                      {ticketType === "user"
                        ? selectedUser.user_email
                        : selectedUser.driver_email}
                    </span>
                  </Label>
                </Grid>
                <Grid item xs={6}>
                  <Label style={{ color: "#4D667C", fontFamily: "Montserrat" }}>
                    Ticket No:
                    {selectedUser.ticket_no}
                  </Label>
                </Grid>
                {/* <Grid item xs={4}>
                  <Label style={{ color: "#4D667C", fontFamily: "Montserrat" }}>
                    Department: {selectedUser.department}
                  </Label>
                </Grid> */}
                <Grid item xs={6} textAlign="right">
                  <Label style={{ color: "#4D667C", fontFamily: "Montserrat" }}>
                    Status :
                  </Label>
                  <Value
                    sx={{
                      color:
                        selectedUser.status === "Resolved"
                          ? "#1FA541"
                          : selectedUser.status === "Processing"
                          ? "#FF9500"
                          : "orange", // default color
                      fontWeight: "bold",
                      fontFamily: "Montserrat",
                    }}
                  >
                    {selectedUser.status}
                  </Value>
                </Grid>
                <Grid item xs={12}>
                  <Label
                    style={{
                      color: "#4D667C",
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                    }}
                  >
                    Subject :
                  </Label>
                  <Value style={{ color: "#4D667C", fontFamily: "Montserrat" }}>
                    {selectedUser.subject}
                  </Value>
                </Grid>
                <Grid item xs={12}>
                  <Label
                    style={{
                      color: "#4D667C",
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                    }}
                  >
                    Message :
                  </Label>
                  <Value
                    sx={{
                      color: "#4D667C",
                      lineHeight: 1.5,
                      fontFamily: "Montserrat",
                      whiteSpace: "normal", // Allows the text to wrap
                      wordWrap: "break-word", // Breaks long words to prevent overflow
                    }}
                  >
                    {selectedUser.message}
                  </Value>
                </Grid>
                <Grid item xs={12}>
                  <Label
                    style={{
                      color: "#4D667C",
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                    }}
                  >
                    Attachments:
                  </Label>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    gutterBottom
                    style={{
                      color: "#344054",
                      fontWeight: "bold",
                      fontFamily: "Montserrat",
                    }}
                  >
                    Screenshot
                  </Typography>
                  <AttachmentBox>
                    {renderAttachment(selectedUser.screenshot)}
                  </AttachmentBox>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    gutterBottom
                    style={{
                      color: "#344054",
                      fontWeight: "bold",
                      fontFamily: "Montserrat",
                    }}
                  >
                    Other attachments
                  </Typography>
                  <AttachmentBox>
                    {renderAttachment(selectedUser.other_attachments)}
                  </AttachmentBox>
                </Grid>
                <Grid item xs={12} textAlign="center">
                  {selectedUser.status !== "Resolved" && (
                    <Button
                      disabled={updatingStatus}
                      onClick={() => handleStatusUpdate(selectedUser.id)}
                      style={{
                        marginTop: "16px",
                        backgroundColor: updatingStatus ? "#ccc" : "orange",
                        paddingLeft: "16px", //
                        paddingRight: "16px",
                        outline: "none",
                        fontFamily: "Montserrat",
                        border: "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!updatingStatus) {
                          (
                            e.target as HTMLButtonElement
                          ).style.backgroundColor = "#F78409";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!updatingStatus) {
                          (
                            e.target as HTMLButtonElement
                          ).style.backgroundColor = "orange";
                        }
                      }}
                    >
                      {updatingStatus ? "Updating" : "Mark as Resolved"}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
          </Modal>
        )}
      </Box>
    </div>
  );
};

export default SupportTicket;
